import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { Form } from "./FormService";
import { SubmissionFile } from "./SubmissionFileService";

// GraphQL queries
const SUBMISSION_LIST_QUERY = gql`
  query SubmissionList(
    $filter: SubmissionFilter
    $params: PaginationAndSorting
  ) {
    submissionList(filter: $filter, params: $params) {
      code
      data {
        id
        formId
        submissionDttm
        requestId
        remoteIp
        userAgent
        referrer
        sentToEmail
        sentToUrl
        redirectUrl
        formData
        filesCnt
        filesSize
        gibberishScore
        spamScore
        spamEngine
        spamReason
        status
        rights
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SUBMISSION_GET_QUERY = gql`
  query SubmissionGet($id: ID) {
    submissionGet(id: $id) {
      code
      data {
        id
        formId
        submissionDttm
        requestId
        remoteIp
        userAgent
        referrer
        sentToEmail
        sentToUrl
        redirectUrl
        formData
        filesCnt
        filesSize
        gibberishScore
        spamScore
        spamEngine
        spamReason
        status
        rights
        createdAt
        updatedAt
        form {
          id
          name
          path
          status
        }
        submissionFiles {
          id
          formFieldName
          fileName
          mimeType
          fileSize
          fileUrl
          errorDescription
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SUBMISSION_GET_MANY_QUERY = gql`
  query SubmissionGetMany($ids: [ID]) {
    submissionGetMany(ids: $ids) {
      code
      data {
        id
        formId
        submissionDttm
        requestId
        remoteIp
        userAgent
        referrer
        sentToEmail
        sentToUrl
        redirectUrl
        formData
        filesCnt
        filesSize
        gibberishScore
        spamScore
        spamEngine
        spamReason
        status
        rights
        createdAt
        updatedAt
        form {
          id
          name
          path
          status
        }
        submissionFiles {
          id
          formFieldName
          fileName
          mimeType
          fileSize
          fileUrl
          errorDescription
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const SUBMISSION_UPDATE_MUTATION = gql`
  mutation SubmissionUpdate(
    $where: SubmissionWhereInput
    $params: SubmissionInput
  ) {
    submissionUpdate(where: $where, params: $params) {
      code
      data {
        id
        formId
        submissionDttm
        requestId
        remoteIp
        userAgent
        referrer
        sentToEmail
        sentToUrl
        redirectUrl
        formData
        filesCnt
        filesSize
        gibberishScore
        spamScore
        spamEngine
        spamReason
        status
        rights
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SUBMISSION_REMOVE_MUTATION = gql`
  mutation SubmissionRemove($where: SubmissionWhereInput) {
    submissionRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SUBMISSION_REMOVE_MANY_MUTATION = gql`
  mutation SubmissionRemoveMany($where: [SubmissionWhereInput]) {
    submissionRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface Submission {
  id?: string | undefined;
  formId?: string | undefined;
  submissionDttm?: string | undefined;
  requestId?: string | undefined;
  remoteIp?: string | undefined;
  userAgent?: string | undefined;
  referrer?: string | undefined;
  sentToEmail?: string | undefined;
  sentToUrl?: string | undefined;
  redirectUrl?: string | undefined;
  formData?: string | undefined;
  filesCnt?: number | undefined;
  filesSize?: string | undefined;
  gibberishScore?: number | undefined;
  spamScore?: number | undefined;
  spamEngine?: string | undefined;
  spamReason?: string | undefined;
  status?: number | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  form?: Form;
  submissionFiles?: SubmissionFile[];
  rights?: string[];
}

export interface SubmissionInput {
  id?: string | null | undefined;
  formId?: string | null | undefined;
  sentToEmail?: string | null | undefined;
  sentToUrl?: string | null | undefined;
  redirectUrl?: string | null | undefined;
  status?: number | null | undefined;
}

export interface SubmissionFilter {
  id?: string[] | null | undefined;
  formId?: string[] | null | undefined;
  requestId?: string[] | null | undefined;
  status?: number[] | null | undefined;
  hasFiles?: boolean | null | undefined;
  minFilesSize?: number | null | undefined;
  maxFilesSize?: number | null | undefined;
  fromDate?: string | null | undefined;
  toDate?: string | null | undefined;
  searchKeyword: String;
}

export interface SubmissionWhereInput {
  id?: string | undefined;
}

/**
 * Submission service that handles all submission-related API calls
 * Supports operations: list, get, getMany, update, remove, removeMany
 */
export class SubmissionService {
  /**
   * Get a list of submissions with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: SubmissionFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(SUBMISSION_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single submission by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(SUBMISSION_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple submissions by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(SUBMISSION_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Update an existing submission
   */
  static async update(
    where: SubmissionWhereInput,
    params: SubmissionInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      SUBMISSION_UPDATE_MUTATION,
      {
        where,
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete a submission
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      SUBMISSION_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple submissions
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      SUBMISSION_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
